/**
 * @fileoverview Initializes and configures the logo slider using the Swiper library.
 * This implementation uses the Autoplay and Navigation modules to provide
 * automatic sliding and manual navigation functionality.
 *
 * @module sliderLogos
 * @author Dreamers
 * @description
 * This module sets up a Swiper instance to display a responsive carousel
 * for logos, adapting the number of visible slides based on screen size.
 */

import Swiper, { Autoplay, Navigation } from 'swiper';

/**
 * Initializes a Swiper instance for logo sliders.
 *
 * @function sliderLogos
 * @memberof module:sliderLogos
 * @returns {void} This function does not return a value.
 *
 * @example
 * // Example usage:
 * import sliderLogos from './sliderLogos.js';
 * sliderLogos();
 *
 * @description
 * This function:
 * 1. Registers the Swiper modules (Autoplay, Navigation).
 * 2. Creates a new Swiper instance targeting `.o-logos-1__slider-container`.
 * 3. Configures autoplay, loop, navigation, and responsive breakpoints.
 *
 * The function ensures touch movement is disabled and cursor grabbing is off
 * to maintain consistent UX on both desktop and mobile screens.
 */
function sliderLogos() {
	// Register the necessary Swiper modules for autoplay and navigation.
	// This ensures the Swiper instance can use these features properly.
	Swiper.use([Autoplay, Navigation]);

	// Initialize the Swiper instance with detailed configuration.
	// The `.o-logos-1__slider-container` class targets the logo slider element.
	new Swiper('.o-logos-1__slider-container', {
		// Enables continuous loop scrolling.
		loop: true,

		// Controls the animation speed in milliseconds.
		speed: 400,

		// Enables automatic slide transitions.
		autoplay: {
			delay: 2000,
			disableOnInteraction: false,
		},

		// Disables the hand cursor on hover for consistency.
		grabCursor: false,

		// Prevents users from manually dragging the slides.
		allowTouchMove: true,

		// Defines how many slides are visible at once.
		slidesPerView: 1,

		// Sets spacing between slides in pixels.
		spaceBetween: 12,

		// Navigation buttons allow manual slide control.
		navigation: {
			prevEl: '.js-slider-btn-prev',
			nextEl: '.js-slider-btn-next',
		},

		// Responsive breakpoints for adaptive slide counts.
		breakpoints: {
			// When the viewport width is 640px or more,
			// show 2 slides at a time.
			640: {
				slidesPerView: 2,
			},

			// When the viewport width is 1024px or more,
			// show 3 slides at a time.
			1024: {
				slidesPerView: 3,
			},

			// When the viewport width is 1280px or more,
			// show 4 slides at a time.
			1280: {
				slidesPerView: 4,
			},
		},
	});
}

// Export the slider initialization function for use elsewhere in the project.
export default sliderLogos;
